import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  MicrosoftOutlookLogo, Check, Warning, ArrowSquareOut, Plug, Stamp, CaretRight,
} from "@phosphor-icons/react";

/**
 * `/settings/integrations` — solicitor-facing page to wire up third-party
 * connections per-user. Initial scope:
 *   - Microsoft 365 / Outlook (send-from-my-mailbox + calendar sync)
 *
 * When the backend doesn't yet have Azure credentials set, we show a clear
 * "Awaiting admin setup" message rather than a broken button.
 *
 * On return from Microsoft's consent flow the URL will contain
 * ?outlook=connected | error_code — we surface that as a toast then strip
 * the params from the URL so a refresh doesn't re-fire.
 */
export default function IntegrationsSettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "solicitor") {
      toast.error("Solicitor access required");
      navigate("/");
    }
  }, [user, navigate]);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/outlook/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load Outlook status");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { if (user?.role === "solicitor") fetchStatus(); }, [user, fetchStatus]);

  // Surface callback flag from /auth/microsoft/callback redirect.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flag = params.get("outlook");
    if (!flag) return;
    if (flag === "connected") {
      const as = params.get("as");
      toast.success(`Outlook connected${as ? ` as ${as}` : ""}`);
      fetchStatus();
    } else {
      const detail = params.get("detail") || flag;
      toast.error(`Outlook connect failed: ${detail}`);
    }
    // Strip the params so a refresh doesn't re-fire the toast.
    navigate(location.pathname, { replace: true });
  }, [location, navigate, fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await API.post("/outlook/auth/start");
      // Full-page redirect — bring us to Microsoft's consent screen.
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start Outlook connect");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect your Outlook mailbox? Sent emails will revert to ConveyTrack's default email provider.")) return;
    try {
      await API.post("/outlook/disconnect");
      toast.success("Outlook disconnected");
      fetchStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Disconnect failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="integrations-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Integrations
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Connect your own accounts to ConveyTrack. Connections are per-user; only you can see and manage yours.
            </p>
          </div>
          {user?.is_admin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/settings/integrations/health")}
              data-testid="firm-health-link"
            >
              Firm Connection Health
            </Button>
          )}
        </div>

        {/* Submission credentials shortcut — firm-level presenter creds
            for HMRC SDLT, HMLR AP1, Companies House MR01 and Thirdfort. */}
        <div
          onClick={() => navigate("/settings/submissions")}
          className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 mb-4 cursor-pointer hover:border-[#1462a0]/60 hover:shadow transition"
          data-testid="submission-settings-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 bg-[#1462a0]/10 text-[#1462a0]">
              <Stamp weight="duotone" className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Submission Credentials
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Companies House Presenter ID + Auth Code (for MR01), HMRC SDLT, HMLR Key Number, Thirdfort API.
                Required to file on behalf of clients.
              </p>
            </div>
            <CaretRight weight="bold" className="w-4 h-4 text-slate-400 shrink-0" />
          </div>
        </div>

        {/* Outlook card */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="outlook-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(0,120,212,0.1)" }}>
              <MicrosoftOutlookLogo weight="fill" className="w-7 h-7" style={{ color: "#0078d4" }} />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Microsoft 365 / Outlook
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Send case emails from your own Outlook mailbox (they appear in <em>your</em> Sent Items) and mirror key dates to your Outlook calendar.
              </p>
              <div className="mt-3 text-xs text-slate-500">
                {loading ? (
                  <span className="text-slate-400">Checking status…</span>
                ) : !status?.configured ? (
                  <span className="inline-flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2 py-1 rounded-md" data-testid="outlook-not-configured-pill">
                    <Warning weight="fill" className="w-3.5 h-3.5" />
                    Awaiting admin setup — the firm's Azure app credentials haven't been added yet.
                  </span>
                ) : status?.connected ? (
                  status?.needs_reconnect ? (
                    <span className="inline-flex items-center gap-1.5 text-red-700 bg-red-50 px-2 py-1 rounded-md" data-testid="outlook-needs-reconnect-pill">
                      <Warning weight="fill" className="w-3.5 h-3.5" />
                      Connection expired — please reconnect ({status.email})
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md" data-testid="outlook-connected-pill">
                      <Check weight="bold" className="w-3.5 h-3.5" />
                      Connected as <span className="font-medium">{status.email}</span>
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-testid="outlook-disconnected-pill">
                    <Plug weight="bold" className="w-3.5 h-3.5" />
                    Not connected yet
                  </span>
                )}
              </div>
            </div>
            <div className="shrink-0">
              {!loading && status?.configured && (
                status.connected ? (
                  <div className="flex items-center gap-2">
                    {status.needs_reconnect && (
                      <Button
                        onClick={handleConnect}
                        disabled={connecting}
                        className="text-white gap-1.5 bg-red-600 hover:bg-red-700"
                        data-testid="outlook-reconnect-btn"
                      >
                        {connecting ? "Redirecting…" : (<><MicrosoftOutlookLogo weight="bold" className="w-4 h-4" /> Reconnect <ArrowSquareOut weight="bold" className="w-3 h-3" /></>)}
                      </Button>
                    )}
                    <Button
                      onClick={handleDisconnect}
                      variant="outline"
                      className="border-red-200 text-red-700 hover:bg-red-50"
                      data-testid="outlook-disconnect-btn"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="text-white gap-1.5"
                    style={{ background: "#0078d4" }}
                    data-testid="outlook-connect-btn"
                  >
                    {connecting ? "Redirecting…" : (<><MicrosoftOutlookLogo weight="bold" className="w-4 h-4" /> Connect Outlook <ArrowSquareOut weight="bold" className="w-3 h-3" /></>)}
                  </Button>
                )
              )}
            </div>
          </div>

          {/* Capabilities checklist */}
          {!loading && status?.configured && (
            <ul className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600" data-testid="outlook-capabilities">
              <li className="flex items-start gap-2">
                <Check weight="bold" className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                <span>Send case-update emails from your Outlook (lands in your Sent Items)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check weight="bold" className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                <span>Exchange & completion dates auto-create calendar events</span>
              </li>
              <li className="flex items-start gap-2 text-slate-400">
                <Plug weight="bold" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span><em>Coming soon</em>: inbox sync (incoming replies auto-attached to the right case)</span>
              </li>
              <li className="flex items-start gap-2">
                <Check weight="bold" className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                <span>Per-user — only you can read or send from your mailbox</span>
              </li>
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
